import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { shallow } from 'zustand/shallow';
import { useUsersStore } from '../../store/useUsersStore';


const ProjectBudget = ({ subUserId: propSubUserId }) => {
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const storeError = useUsersStore((s) => s.error);
  useUsersStore(shallow);


  // ============ RESOLVE SUB-USER ID ============
  const resolvedSubUserId = useMemo(() => {
    if (propSubUserId) return String(propSubUserId);
    if (!selectedKey || !Array.isArray(users)) return null;
    const found = users.find((u) => {
      const id = u.sub_user_id || u.document_id || u.id;
      if (String(id) === String(selectedKey)) return true;
      if (u.sub_username && String(u.sub_username) === String(selectedKey)) return true;
      return false;
    });
    return found ? String(found.sub_user_id || found.document_id || found.id) : null;
  }, [propSubUserId, selectedKey, users]);


  const finalSubUserId = useMemo(
    () => String(resolvedSubUserId || propSubUserId || selectedKey || '') || null,
    [resolvedSubUserId, propSubUserId, selectedKey]
  );


  // ============ GET PROJECT BUDGET ============
  const projectBudget = useMemo(() => {
    if (!finalSubUserId || !Array.isArray(users)) return {};
    const u = users.find((x) =>
      String(x.sub_user_id || x.document_id || x.id) === String(finalSubUserId) ||
      (x.sub_username && String(x.sub_username) === String(finalSubUserId))
    );
    return (u && u.project_budget) || {};
  }, [finalSubUserId, users]);


  // ============ INITIAL VALUES ============
  const initialValues = useMemo(() => {
    const mf = projectBudget.min_fixed_budget;
    const mh = projectBudget.min_hourly_budget;
    const minFixed = (mf === undefined || mf === null || mf === '') ? 0 : Number(mf) || 0;
    const minHourly = (mh === undefined || mh === null || mh === '') ? 0 : Number(mh) || 0;
    return { minFixed, minHourly };
  }, [projectBudget.min_fixed_budget, projectBudget.min_hourly_budget]);


  const snapshotJson = useMemo(() => JSON.stringify(initialValues), [initialValues]);


  // ============ LOCAL STATE ============
  const [minFixed, setMinFixed] = useState(initialValues.minFixed);
  const [minHourly, setMinHourly] = useState(initialValues.minHourly);
  const prevSnapshotRef = useRef(snapshotJson);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);


  // ============ SYNC FROM STORE ============
  useEffect(() => {
    if (prevSnapshotRef.current !== snapshotJson) {
      setMinFixed(initialValues.minFixed);
      setMinHourly(initialValues.minHourly);
      prevSnapshotRef.current = snapshotJson;
    }
    setMsg(null);
    setErr(null);
  }, [snapshotJson, finalSubUserId, initialValues.minFixed, initialValues.minHourly]);


  // ============ HANDLERS ============
  const handleChangeMinFixed = useCallback((e) => {
    const v = e.target.value;
    setMinFixed(v === '' ? '' : Number(v));
  }, []);


  const handleChangeMinHourly = useCallback((e) => {
    const v = e.target.value;
    setMinHourly(v === '' ? '' : Number(v));
  }, []);


  const handleSubmit = useCallback(async () => {
    setErr(null);
    setMsg(null);
    if (!finalSubUserId) {
      setErr('No sub-user selected');
      return;
    }


    const payload = {
      project_budget: {
        min_fixed_budget: Number(minFixed) || 0,
        min_hourly_budget: Number(minHourly) || 0,
      },
    };


    setSaving(true);
    try {
      await updateSubUser(finalSubUserId, payload);
      setMsg('✅ Saved successfully');
    } catch (e) {
      console.error('Failed to save project_budget', e);
      setErr(`❌ ${e?.message || 'Save failed'}`);
    } finally {
      setSaving(false);
    }
  }, [finalSubUserId, minFixed, minHourly, updateSubUser]);


  const handleReset = useCallback(() => {
    setMinFixed(initialValues.minFixed);
    setMinHourly(initialValues.minHourly);
    setMsg(null);
    setErr(null);
  }, [initialValues]);


  // ============ RENDER ============
  return (
    <div className="w-full p-6 bg-white rounded-lg shadow">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Manage Project Budget</h2>
        <p className="text-sm text-gray-600 mt-2">
          Set minimum budget thresholds to filter projects.
        </p>
      </div>


      {/* Info Alert */}
      {/* <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              You will only see projects whose minimum budget is equal to or greater than your entered values.
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Base currency is USD. For other currencies, budget will be converted as per the latest conversion rate.
            </p>
          </div>
        </div>
      </div> */}


      {/* Status Messages */}
      {(msg || err || storeError) && (
        <div className="mb-6 flex items-center gap-2">
          {saving && <span className="text-blue-600 text-sm font-medium">⏳ Saving...</span>}
          {msg && !saving && <span className="text-green-600 text-sm font-medium">{msg}</span>}
          {err && !saving && <span className="text-red-600 text-sm font-medium">{err}</span>}
          {storeError && !err && !saving && <span className="text-red-600 text-sm font-medium">{storeError}</span>}
        </div>
      )}


      {/* Budget Input Fields - Figma Style */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Min Fixed Budget */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Min fixed budget (in USD)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={minFixed === '' ? '' : minFixed}
            onChange={handleChangeMinFixed}
            placeholder="i.e. $200"
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-2 focus:border-primary-blue transition-all"
          />
          <p className="text-xs text-gray-500 mt-1">
            Minimum fixed project budget threshold
          </p>
        </div>


        {/* Min Hourly Budget */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Min hourly budget (in USD)
          </label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={minHourly === '' ? '' : minHourly}
            onChange={handleChangeMinHourly}
            placeholder="i.e. $20"
            className="w-full border-[1.5px] border-primary-blue rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-2 focus:border-primary-blue transition-all"
          />
          <p className="text-xs text-gray-500 mt-1">
            Minimum hourly project budget threshold
          </p>
        </div>
      </div>


      {/* Current Values Display */}
      {(minFixed > 0 || minHourly > 0) && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-600 font-medium mb-2">Current Budget Filters:</p>
          <div className="flex flex-wrap gap-3">
            {minFixed > 0 && (
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                Fixed: ${minFixed}
              </div>
            )}
            {minHourly > 0 && (
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                Hourly: ${minHourly}/hr
              </div>
            )}
          </div>
        </div>
      )}


      {/* Footer Buttons - Matching Currency Filter */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleReset}
          disabled={saving}
          className="px-5 py-2 border-[1.5px] border-primary-blue text-primary-blue rounded-lg hover:bg-primary-blue hover:text-white disabled:opacity-60 transition-all font-medium"
        >
          Reset
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving || !finalSubUserId}
          className="px-6 py-2.5 bg-primary-blue text-white rounded-lg hover:bg-primary-blue/80 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all font-medium"
        >
          {saving ? ' Saving...' : ' Save Changes'}
        </button>
      </div>
    </div>
  );
};


ProjectBudget.propTypes = {
  subUserId: PropTypes.string,
};


export default memo(ProjectBudget);
